import { formatAssigneeDisplayName } from './approvalAssigneeDisplay';

const ACTION_META = {
  submit: { role: '提交申请', action: '提交', done: true },
  approve: { role: '审批人', action: '同意', done: true },
  reject: { role: '审批人', action: '驳回', done: true },
  transfer: { role: '审批人', action: '转交', done: true },
  add_sign: { role: '审批人', action: '加签', done: true },
  return: { role: '审批人', action: '退回', done: true },
  revoke: { role: '发起人', action: '撤销', done: true }
};

function parseTransferTargets(comment) {
  const m = comment?.match(/转交给[：:]\s*(.+?)(?:；|$)/);
  if (!m) return [];
  return m[1].split('、').map((s) => s.trim()).filter(Boolean);
}

function parseAddSignInfo(comment) {
  const pre = comment?.match(/前加签[：:]\s*(.+?)(?:；|$)/);
  const post = comment?.match(/后加签[：:]\s*(.+?)(?:；|$)/);
  if (pre) {
    return { mode: 'pre', names: pre[1].split('、').map((s) => s.trim()).filter(Boolean) };
  }
  if (post) {
    return { mode: 'post', names: post[1].split('、').map((s) => s.trim()).filter(Boolean) };
  }
  return { mode: null, names: [] };
}

function buildAddSignParticipantFromTask(task, overrides = {}) {
  const isAddSign = task.add_sign_type === 'pre' || task.add_sign_type === 'post';
  const done = task.status === 'done';
  return {
    user_name: formatAssigneeDisplayName(task, 'approver'),
    role_label: '审批人',
    action_label: done ? (isAddSign ? '加签' : '同意') : (overrides.action_label ?? null),
    status: done ? 'done' : (overrides.status ?? 'pending'),
    done,
    task_id: task.id,
    comment: overrides.comment ?? null,
    create_time: overrides.create_time ?? (done ? (task.done_time || task.create_time) : task.create_time) ?? null,
    is_operator: overrides.is_operator === true
  };
}

function buildAddSignParticipantPlaceholder(name, overrides = {}) {
  return {
    user_name: name,
    role_label: '审批人',
    action_label: overrides.action_label ?? null,
    status: overrides.status ?? 'pending',
    done: overrides.done === true,
    task_id: overrides.task_id ?? null,
    comment: overrides.comment ?? null,
    create_time: overrides.create_time ?? null,
    is_operator: overrides.is_operator === true
  };
}

/** 将审批记录中的意见匹配到加签块参与者（与 shouldSkipApproveLog 时间窗一致） */
function findParticipantApprovalComment(approverName, task, approvals) {
  if (!task || task.status !== 'done' || !approverName) return null;

  const doneTime = task.done_time ? new Date(task.done_time).getTime() : null;
  const candidates = (approvals || []).filter(
    (a) => a.approver_name === approverName && a.action === 'approve'
  );
  if (!candidates.length) return null;

  let matched = null;
  if (doneTime != null) {
    let bestDiff = Infinity;
    candidates.forEach((a) => {
      const diff = Math.abs(new Date(a.create_time).getTime() - doneTime);
      if (diff < 120000 && diff < bestDiff) {
        bestDiff = diff;
        matched = a;
      }
    });
  }
  if (!matched && candidates.length === 1) {
    matched = candidates[0];
  }

  const text = matched?.comment != null ? String(matched.comment).trim() : '';
  return text || null;
}

function enrichAddSignParticipantWithApproval(participant, task, approvals) {
  if (!task || task.status !== 'done') return participant;
  const comment = findParticipantApprovalComment(participant.user_name, task, approvals);
  return {
    ...participant,
    comment: comment || participant.comment || null,
    create_time: task.done_time || participant.create_time || null
  };
}

function findOperatorMainTask(approval, approveTasks, usedTaskIds) {
  const operatorName = approval.approver_name;
  const approvalTime = new Date(approval.create_time).getTime();

  const candidates = approveTasks.filter((t) => {
    if (usedTaskIds.has(t.id)) return false;
    if (t.add_sign_type) return false;
    if (t.assignee_name !== operatorName) return false;
    if (t.status === 'cancelled') return false;
    return true;
  });

  const afterSign = candidates.find((t) => {
    const ct = new Date(t.create_time).getTime();
    return ct >= approvalTime - 120000;
  });
  if (afterSign) return afterSign;

  return candidates.find((t) => t.status === 'pending') || candidates.find((t) => t.status === 'done') || null;
}

function markAddSignGroupProcessing(participants) {
  let pickIdx = -1;
  for (let i = 0; i < participants.length; i += 1) {
    if (!participants[i].done) {
      pickIdx = i;
      break;
    }
  }
  return participants.map((p, i) => {
    if (p.done) return p;
    if (i === pickIdx) {
      return { ...p, status: 'current', action_label: '处理中' };
    }
    return {
      ...p,
      status: 'pending',
      action_label: p.action_label === '处理中' ? null : p.action_label
    };
  });
}

function resolveAddSignGroupStatus(participants) {
  if (!participants.length) return { status: 'pending', done: false };
  if (participants.every((p) => p.done)) return { status: 'done', done: true };
  if (participants.some((p) => p.status === 'current')) return { status: 'current', done: false };
  return { status: 'pending', done: false };
}

function buildAddSignGroupItem(approval, approveTasks, usedTaskIds, shownNames) {
  const { mode, names } = parseAddSignInfo(approval.comment);
  if (!mode || !names.length) return null;

  const operatorName = approval.approver_name;
  const relatedTasks = matchTasksForApproval(approval, approveTasks, usedTaskIds);
  relatedTasks.forEach((t) => {
    usedTaskIds.add(t.id);
    shownNames.add(t.assignee_name);
  });

  const operatorTask = findOperatorMainTask(approval, approveTasks, usedTaskIds);
  if (operatorTask) {
    usedTaskIds.add(operatorTask.id);
    shownNames.add(operatorTask.assignee_name);
  }

  const addSignParticipants = names.map((name) => {
    const task = relatedTasks.find((t) => t.assignee_name === name);
    if (task) return buildAddSignParticipantFromTask(task);
    return buildAddSignParticipantPlaceholder(name, {
      action_label: mode === 'post' ? '待加签' : null
    });
  });

  let operatorParticipant;
  if (operatorTask) {
    operatorParticipant = buildAddSignParticipantFromTask(operatorTask, { is_operator: true });
  } else {
    operatorParticipant = buildAddSignParticipantPlaceholder(operatorName, {
      is_operator: true,
      action_label: mode === 'post' ? '处理中' : null,
      status: mode === 'post' ? 'current' : 'pending'
    });
  }

  let participants = mode === 'pre'
    ? [...addSignParticipants, operatorParticipant]
    : [operatorParticipant, ...addSignParticipants];

  participants = markAddSignGroupProcessing(participants);
  const groupStatus = resolveAddSignGroupStatus(participants);

  return enrichProgressItem({
    key: `addsign-group-${approval.id}`,
    type: 'add_sign_group',
    is_add_sign_group: true,
    add_sign_mode: mode,
    add_sign_names: names,
    user_name: `${participants.length}位参与人`,
    role_label: '审批人',
    action_label: null,
    comment: null,
    create_time: approval.create_time,
    status: groupStatus.status,
    done: groupStatus.done,
    show_action: false,
    participants,
    participant_count: participants.length,
    queueKind: QUEUE_KIND.MAIN,
    approval_id: approval.id
  }, 'approver');
}

function refreshAddSignGroups(items, approveTasks, approvals = [], options = {}) {
  const { finished = false } = options;
  const taskMap = Object.fromEntries(approveTasks.map((t) => [t.id, t]));
  const usedInRefresh = new Set();

  return items.map((item) => {
    if (!item.is_add_sign_group) return item;

    const signType = item.add_sign_mode === 'post' ? 'post' : 'pre';

    let participants = item.participants.map((p) => {
      let task = null;
      if (p.task_id && taskMap[p.task_id]) {
        task = taskMap[p.task_id];
        usedInRefresh.add(p.task_id);
      } else {
        task = approveTasks.find((t) => (
          !usedInRefresh.has(t.id)
          && t.add_sign_type === signType
          && t.assignee_name === p.user_name
          && t.status !== 'cancelled'
        )) || null;
        if (task) usedInRefresh.add(task.id);
      }

      if (task) {
        const base = buildAddSignParticipantFromTask(task, { is_operator: p.is_operator });
        return enrichAddSignParticipantWithApproval(base, task, approvals);
      }
      return p;
    });

    const operator = participants.find((p) => p.is_operator);
    if (operator && !operator.task_id) {
      const operatorTask = approveTasks.find((t) => (
        !usedInRefresh.has(t.id)
        && !t.add_sign_type
        && t.assignee_name === operator.user_name
        && t.status !== 'cancelled'
      ));
      if (operatorTask) {
        usedInRefresh.add(operatorTask.id);
        participants = participants.map((p) => {
          if (!p.is_operator) return p;
          const base = buildAddSignParticipantFromTask(operatorTask, { is_operator: true });
          return enrichAddSignParticipantWithApproval(base, operatorTask, approvals);
        });
      }
    }

    if (item.add_sign_mode === 'post') {
      const op = participants.find((p) => p.is_operator);
      participants = participants.map((p) => {
        if (p.is_operator || p.task_id) return p;
        if (op?.done) return { ...p, action_label: null };
        return { ...p, action_label: '待加签' };
      });
    }

    // 流程已结束（正常审批完成）时，加签节点不应再出现「处理中」：
    // 结束时会取消剩余 pending 的加签任务，这些任务不在 approveTasks 中，
    // 导致参与人退化为无任务的占位并被 markAddSignGroupProcessing 误标为处理中。
    // 这里统一按已完成展示，避免最后一个加签节点一直停在「处理中」。
    if (finished) {
      const completedParticipants = participants.map((p) => {
        if (p.done || p.status === 'done') return p;
        const label = p.action_label && p.action_label !== '处理中' ? p.action_label : '同意';
        return { ...p, status: 'done', done: true, action_label: label };
      });
      return {
        ...item,
        participants: completedParticipants,
        participant_count: completedParticipants.length,
        status: 'done',
        done: true,
        user_name: `${completedParticipants.length}位参与人`
      };
    }

    participants = markAddSignGroupProcessing(participants);
    const groupStatus = resolveAddSignGroupStatus(participants);

    return {
      ...item,
      participants,
      participant_count: participants.length,
      status: groupStatus.status,
      done: groupStatus.done,
      user_name: `${participants.length}位参与人`
    };
  });
}

function removeDuplicateAddSignParticipants(items) {
  const groups = items.filter((i) => i.is_add_sign_group);
  if (!groups.length) return items;

  const absorbedTaskIds = new Set();
  groups.forEach((g) => {
    g.participants.forEach((p) => {
      if (p.task_id) absorbedTaskIds.add(p.task_id);
    });
  });

  const activeUsers = new Set();
  groups.forEach((g) => {
    if (g.done) return;
    g.participants.forEach((p) => {
      if (!p.done) activeUsers.add(p.user_name);
    });
  });

  return items.filter((item) => {
    if (item.is_add_sign_group || item.type === 'add_sign') return true;
    if (item.task_id && absorbedTaskIds.has(item.task_id)) return false;
    if (!item.done && activeUsers.has(item.user_name)
        && ['approver', 'add_sign_target', 'transfer_target'].includes(item.type)) {
      return false;
    }
    return true;
  });
}

const QUEUE_KIND = {
  PRE_ADD_SIGN: 'pre_add_sign',
  MAIN: 'main_approver',
  POST_ADD_SIGN: 'post_add_sign',
  FUTURE: 'future'
};

const QUEUE_ORDER = {
  [QUEUE_KIND.PRE_ADD_SIGN]: 0,
  [QUEUE_KIND.MAIN]: 1,
  [QUEUE_KIND.POST_ADD_SIGN]: 2,
  [QUEUE_KIND.FUTURE]: 3
};

const STEP_TYPE_LABELS = {
  approver: '审批人',
  cc: '抄送人',
  executor: '执行人',
  submit: '发起人'
};

function normalizeStepType(type) {
  if (type === 'cc') return 'cc';
  if (type === 'executor') return 'executor';
  if (type === 'submit' || type === 'revoke') return 'submit';
  return 'approver';
}

function enrichProgressItem(item, stepType, nodeLabel = null) {
  const type = normalizeStepType(stepType || item.type);
  const typeLabel = item.type_label || STEP_TYPE_LABELS[type] || STEP_TYPE_LABELS.approver;
  const resolvedNodeLabel = nodeLabel ?? item.node_label ?? null;
  return {
    ...item,
    type,
    type_label: typeLabel,
    node_label: resolvedNodeLabel && resolvedNodeLabel !== typeLabel ? resolvedNodeLabel : null
  };
}

function mapApprovalItem(a) {
  const meta = ACTION_META[a.action] || { role: '审批人', action: a.action, done: true };
  const stepType = a.action === 'submit' || a.action === 'revoke' ? 'submit' : 'approver';
  const isAutoApprove = a.action === 'approve' && String(a.comment || '').trim() === '自动通过';
  const isResubmit = a.action === 'submit' && /重新提交/.test(String(a.comment || ''));
  const roleLabel = a.action === 'submit' || a.action === 'revoke' ? '发起人' : meta.role;
  const actionLabel = isAutoApprove
    ? '自动通过'
    : isResubmit
      ? '重新提交'
      : meta.action;
  return enrichProgressItem({
    key: `approval-${a.id}`,
    type: a.action,
    user_name: a.approver_name || '-',
    role_label: roleLabel,
    action_label: actionLabel,
    comment: a.comment,
    create_time: a.create_time,
    status: 'done',
    done: meta.done !== false,
    show_action: true
  }, stepType);
}

function offsetTime(baseTime, ms) {
  if (!baseTime) return null;
  return new Date(new Date(baseTime).getTime() + ms).toISOString();
}

function mapApproveTaskItem(task, context = {}) {
  const isAddSign = task.add_sign_type === 'pre' || task.add_sign_type === 'post';
  const done = task.status === 'done';
  const isCurrent = context.isCurrent === true;
  let actionLabel = null;
  if (done) {
    actionLabel = isAddSign ? '加签' : '同意';
  } else if (isCurrent) {
    actionLabel = '处理中';
  }
  const time = context.sortTime
    || (done ? (task.done_time || task.create_time) : task.create_time);
  let queueKind = context.queueKind || QUEUE_KIND.MAIN;
  if (!context.queueKind) {
    if (task.add_sign_type === 'pre') queueKind = QUEUE_KIND.PRE_ADD_SIGN;
    else if (task.add_sign_type === 'post') queueKind = QUEUE_KIND.POST_ADD_SIGN;
    else if (context.kind === 'transfer_target') queueKind = QUEUE_KIND.MAIN;
  }
  return enrichProgressItem({
    key: `task-${task.id}`,
    type: isAddSign ? 'add_sign_target' : (context.kind === 'transfer_target' ? 'transfer_target' : 'approver'),
    user_name: formatAssigneeDisplayName(task, 'approver'),
    role_label: '审批人',
    action_label: actionLabel,
    comment: null,
    create_time: time,
    status: done ? 'done' : (isCurrent ? 'current' : 'pending'),
    done,
    show_action: done || isCurrent,
    task_id: task.id,
    queueKind
  }, 'approver', context.nodeLabel || null);
}

function mapPlaceholderTarget(name, suffix, status = 'pending', sortTime = null, queueKind = QUEUE_KIND.MAIN) {
  return {
    key: `placeholder-${name}-${suffix}`,
    type: 'approver',
    user_name: name,
    role_label: '审批人',
    action_label: status === 'current' ? '处理中' : null,
    comment: null,
    create_time: sortTime,
    status,
    done: false,
    show_action: status === 'current',
    queueKind
  };
}

function matchTasksForApproval(approval, approveTasks, usedTaskIds) {
  const approvalTime = new Date(approval.create_time).getTime();
  if (approval.action === 'transfer') {
    const names = parseTransferTargets(approval.comment);
    return approveTasks.filter((t) => {
      if (usedTaskIds.has(t.id)) return false;
      if (t.status === 'cancelled') return false;
      if (t.add_sign_type) return false;
      if (!names.includes(t.assignee_name)) return false;
      const dt = Math.abs(new Date(t.create_time).getTime() - approvalTime);
      return dt < 120000;
    });
  }
  if (approval.action === 'add_sign') {
    const { mode, names } = parseAddSignInfo(approval.comment);
    return approveTasks.filter((t) => {
      if (usedTaskIds.has(t.id)) return false;
      if (t.status === 'cancelled') return false;
      if (t.add_sign_type !== mode) return false;
      return names.includes(t.assignee_name);
    });
  }
  return [];
}

function isTerminalProcessStatus(status) {
  return status === 'cancelled' || status === 'rejected';
}

/** 已撤销/已驳回：隐藏未完成的后续审批节点 */
function filterTerminalProgressItems(items, process) {
  if (!isTerminalProcessStatus(process?.status)) return items;
  return items.filter((item) => item.done || item.status === 'done');
}

function shouldSkipApproveLog(approval, approveTasks, usedTaskIds) {
  if (approval.action !== 'approve') return false;
  const match = approveTasks.find((t) => {
    if (!usedTaskIds.has(t.id)) return false;
    if (t.assignee_name !== approval.approver_name) return false;
    if (t.status !== 'done') return false;
    const dt = Math.abs(new Date(t.done_time || 0) - new Date(approval.create_time));
    return dt < 120000;
  });
  return !!match;
}

function getAssigneeNamesFromStep(step) {
  return (step?.assignees || []).map((u) => u.name).filter(Boolean);
}

function shouldMergeStepAssignees(step) {
  if (!step || step.type === 'cc') return false;
  // 同一节点多人（或签/会签/依次审批）统一合并为一条展示，避免一个配置节点显示成多个节点
  const names = getAssigneeNamesFromStep(step);
  return names.length > 1;
}

function formatGroupAssigneeNames(step) {
  if (step?.awaiting_assignee_pick) return '提交时选择';
  const assignees = (step?.assignees || []).filter((u) => u?.name);
  if (!assignees.length) return '-';
  if (step.multi_mode === 'and' && assignees.some((u) => u.approve_status)) {
    return assignees.map((u) => {
      if (u.approve_status === 'done') return `${u.name} ✓`;
      return u.name;
    }).join('、');
  }
  return assignees.map((u) => u.name).join('、');
}

/** 同一审批/执行节点多人（或签、会签）合并为一条展示 */
function buildGroupStepProgressItem(step, index, options = {}) {
  const {
    status = 'pending',
    isCurrent = false,
    queueKind = QUEUE_KIND.FUTURE,
    keyPrefix = 'step-group',
    createTime = null,
    actionLabel = null,
    taskId = null
  } = options;
  const names = getAssigneeNamesFromStep(step);
  if (!names.length) return null;

  const stepType = normalizeStepType(step.type);
  const nodeLabel = step.label || step.type_label || null;
  const done = status === 'done';
  const resolvedAction = actionLabel ?? (
    done
      ? (stepType === 'executor' ? '已执行' : '同意')
      : (isCurrent ? '处理中' : null)
  );

  return enrichProgressItem({
    key: `${keyPrefix}-${step.step_id || index}`,
    step_id: step.step_id || null,
    step_index: step.step_index ?? index,
    type: stepType,
    user_name: formatGroupAssigneeNames(step),
    role_label: step.label || STEP_TYPE_LABELS[stepType],
    action_label: resolvedAction,
    comment: null,
    create_time: createTime,
    status: isCurrent ? 'current' : status,
    done,
    show_action: done || isCurrent,
    queueKind,
    task_id: taskId,
    multi_mode: step.multi_mode || null,
    multi_mode_label: step.multi_mode_label || null,
    is_group: true
  }, step.type, nodeLabel);
}

function getCcAssigneeNames(step) {
  if (step?.awaiting_assignee_pick) return ['提交时选择'];
  const assignees = (step?.assignees || []).filter((u) => u?.name || u?.id);
  if (assignees.length) {
    return assignees.map((u) => u.name).filter(Boolean);
  }
  if (step?.role_names?.length) {
    return step.role_names;
  }
  return [];
}

/** 同一抄送节点多人合并为一条展示 */
function buildCcProgressItem(step, index, options = {}) {
  const {
    status = 'pending',
    isCurrent = false,
    queueKind = QUEUE_KIND.FUTURE,
    keyPrefix = 'cc',
    createTime = null
  } = options;
  const names = getCcAssigneeNames(step);
  if (!names.length) return null;

  const nodeLabel = step.role_names?.length
    ? step.role_names.join('、')
    : (step.label && step.label !== STEP_TYPE_LABELS.cc ? step.label : null);
  const done = status === 'done';

  return enrichProgressItem({
    key: `${keyPrefix}-${step.step_id || index}`,
    step_id: step.step_id || null,
    step_index: step.step_index ?? index,
    type: 'cc',
    user_name: names.join('、'),
    role_label: step.label || '抄送人',
    action_label: done ? '抄送' : (isCurrent ? '处理中' : null),
    comment: null,
    create_time: createTime,
    status: isCurrent ? 'current' : status,
    done,
    show_action: done || isCurrent,
    queueKind
  }, 'cc', nodeLabel);
}

function buildCcTaskGroupItems(ccTasks) {
  const groups = new Map();
  (ccTasks || []).forEach((t) => {
    const key = t.step_id || `task-${t.id}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(t);
  });

  return Array.from(groups.entries()).map(([key, tasks]) => {
    const names = [...new Set(tasks.map((t) => formatAssigneeDisplayName(t, 'cc')).filter(Boolean))];
    const allDone = tasks.every((t) => t.status === 'done' || t.is_read);
    const latestTime = tasks.reduce((max, t) => {
      const time = t.done_time || t.create_time;
      if (!time) return max;
      return !max || new Date(time) > new Date(max) ? time : max;
    }, null);
    const stepId = tasks[0]?.step_id || null;
    return buildCcProgressItem(
      {
        step_id: stepId,
        assignees: names.map((name) => ({ name }))
      },
      key,
      {
        keyPrefix: 'cc-group',
        status: allDone ? 'done' : 'pending',
        createTime: latestTime,
        queueKind: QUEUE_KIND.MAIN
      }
    );
  }).filter(Boolean);
}

function buildExecuteTaskGroupItems(executeTasks, process) {
  const stepLabelOf = (stepId) => {
    if (!stepId) return '执行人';
    const step = (process?.workflow?.steps || []).find((s) => s.step_id === stepId);
    return step?.label || step?.type_label || '执行人';
  };

  return (executeTasks || []).map((t) => {
    const stepLabel = stepLabelOf(t.step_id);
    return enrichProgressItem({
      key: `execute-task-${t.id}`,
      type: 'executor',
      user_name: formatAssigneeDisplayName(t, 'execute'),
      role_label: stepLabel,
      action_label: t.status === 'done' ? '已执行' : (t.status === 'pending' ? '待执行' : null),
      comment: null,
      create_time: t.done_time || t.create_time,
      status: t.status === 'done' ? 'done' : (t.status === 'pending' ? 'current' : 'pending'),
      done: t.status === 'done',
      show_action: true,
      task_id: t.id,
      step_id: t.step_id || null,
      queueKind: QUEUE_KIND.MAIN
    }, 'executor', stepLabel);
  });
}

function executorProgressKey(item) {
  if (item?.task_id) return `task:${item.task_id}`;
  return `executor:${item.step_id || ''}:${item.user_name || ''}`;
}

/** 按节点维度去重，避免「发起人=后续审批人/执行人」时误隐藏 */
function progressAssigneeKey(item) {
  if (item?.task_id) return `task:${item.task_id}`;
  if (item.type === 'executor') return executorProgressKey(item);
  if (item.step_id) return `${item.type}:${item.step_id}:${item.user_name || ''}`;
  return `${item.type}:${item.user_name || ''}`;
}

function isSameExecutorProgress(a, b) {
  if (!a || !b || a.type !== 'executor' || b.type !== 'executor') return false;
  if (a.task_id && b.task_id) return a.task_id === b.task_id;
  return (a.step_id || '') === (b.step_id || '')
    && (a.user_name || '') === (b.user_name || '');
}

function buildWorkflowExecutorFallbackItems(process, existingItems) {
  const steps = (process.workflow?.steps || [])
    .filter((s) => s.type === 'executor' && s.status !== 'skipped')
    .sort((a, b) => (a.step_index ?? 0) - (b.step_index ?? 0));

  if (!steps.length) return [];

  const tasksByStepId = new Map();
  (process.execute_tasks || []).forEach((t) => {
    const key = t.step_id || '';
    if (!tasksByStepId.has(key)) tasksByStepId.set(key, []);
    tasksByStepId.get(key).push(t);
  });

  const items = [];
  steps.forEach((step, idx) => {
    const stepTasks = tasksByStepId.get(step.step_id || '') || [];
    if (stepTasks.length) return;

    if (existingItems.some((i) => i.type === 'executor' && i.step_id === step.step_id)) {
      return;
    }

    if (shouldMergeStepAssignees(step)) {
      const item = buildGroupStepProgressItem(step, idx, {
        status: 'pending',
        isCurrent: false,
        actionLabel: null,
        queueKind: QUEUE_KIND.FUTURE,
        keyPrefix: 'workflow-executor'
      });
      if (item && !existingItems.some((i) => isSameExecutorProgress(i, item))) {
        items.push({
          ...item,
          action_label: null,
          show_action: false
        });
      }
      return;
    }

    (step.assignees || []).forEach((u, i) => {
      if (!u?.name) return;
      const candidate = {
        type: 'executor',
        step_id: step.step_id || null,
        user_name: u.name
      };
      if (existingItems.some((item) => isSameExecutorProgress(item, candidate))) return;
      items.push(enrichProgressItem({
        key: `workflow-executor-${step.step_id || idx}-${u.id || u.name || i}`,
        type: 'executor',
        user_name: u.name,
        role_label: step.label || STEP_TYPE_LABELS.executor,
        action_label: null,
        comment: null,
        create_time: null,
        status: 'pending',
        done: false,
        show_action: false,
        step_id: step.step_id || null,
        queueKind: QUEUE_KIND.FUTURE
      }, 'executor', step.label || null));
    });
  });
  return items;
}

function shouldSkipRuntimeAssignee(item, shownRoleKeys) {
  if (item.is_group && item.step_id) {
    const groupKey = `group:${item.type}:${item.step_id}`;
    if (shownRoleKeys.has(groupKey)) return true;
    shownRoleKeys.add(groupKey);
    return false;
  }
  const roleKey = progressAssigneeKey(item);
  if (shownRoleKeys.has(roleKey)) return true;
  shownRoleKeys.add(roleKey);
  return false;
}

function isFutureNodeDuplicate(merged, node) {
  if (node.key && merged.some((i) => i.key === node.key)) return true;
  if (node.is_group && node.step_id) {
    return merged.some((i) => i.is_group && i.step_id === node.step_id && i.type === node.type);
  }
  if (node.type === 'cc' && node.step_id) {
    return merged.some((i) => i.type === 'cc' && i.step_id === node.step_id);
  }
  if (node.type === 'executor') {
    if (node.is_group && node.step_id) {
      return merged.some((i) => i.type === 'executor' && i.is_group && i.step_id === node.step_id);
    }
    return merged.some((i) => isSameExecutorProgress(i, node));
  }
  if (node.step_id && node.type === 'approver') {
    return merged.some((i) => i.type === 'approver' && i.step_id === node.step_id);
  }
  return merged.some(
    (i) => i.user_name === node.user_name
      && !i.done
      && i.type !== 'cc'
      && i.type !== 'submit'
      && i.type !== 'executor'
      && node.type !== 'executor'
      && node.queueKind !== QUEUE_KIND.FUTURE
      && !node.step_id
  );
}

const ACTIVE_APPROVER_TYPES = new Set([
  'approver', 'transfer_target', 'add_sign_target', 'add_sign', 'transfer', 'approve'
]);

function splitProgressUserNames(userName) {
  if (!userName) return [];
  return String(userName).split('、').map((s) => s.trim()).filter(Boolean);
}

function collectAddSignParticipantNames(groups) {
  const names = new Set();
  groups.forEach((group) => {
    (group.participants || []).forEach((p) => {
      if (p.user_name) names.add(p.user_name);
    });
  });
  return names;
}

/** 加签操作节点后紧跟对应的多人审批块（避免 sortDoneThenPending 把二者拆开） */
function reorderAddSignGroupAfterAction(items) {
  const result = [...items];
  for (let i = 0; i < result.length; i += 1) {
    const item = result[i];
    if (item.type !== 'add_sign' || item.action_label !== '加签') continue;
    const approvalId = String(item.key || '').replace(/^approval-/, '');
    if (!approvalId) continue;
    const groupKey = `addsign-group-${approvalId}`;
    const groupIdx = result.findIndex((x) => x.key === groupKey);
    if (groupIdx < 0 || groupIdx === i + 1) continue;
    const [group] = result.splice(groupIdx, 1);
    result.splice(i + 1, 0, group);
    i += 1;
  }
  return result;
}

/** 加签块未完成时，同节点其他或签/会签审批人延后展示，避免插在加签块中间 */
function deferPeerApproversDuringAddSign(items) {
  const activeGroups = items.filter((item) => item.is_add_sign_group && !item.done);
  if (!activeGroups.length) return items;

  const participantNames = collectAddSignParticipantNames(activeGroups);
  const groupKeys = new Set(activeGroups.map((g) => g.key));

  const shouldDefer = (item) => {
    if (item.done || item.is_add_sign_group || item.type === 'add_sign' || item.deferred_by_add_sign) {
      return false;
    }
    if (item.queueKind === QUEUE_KIND.FUTURE || item.type === 'cc' || item.type === 'submit' || item.type === 'executor') {
      return false;
    }
    const isApproverLike = ACTIVE_APPROVER_TYPES.has(item.type)
      || item.type_label === '审批人'
      || item.is_group;
    if (!isApproverLike) return false;

    const names = splitProgressUserNames(item.user_name);
    if (!names.length) return false;
    return names.some((name) => !participantNames.has(name));
  };

  const kept = [];
  const deferred = [];
  items.forEach((item) => {
    if (shouldDefer(item)) {
      deferred.push({
        ...item,
        status: 'pending',
        action_label: null,
        show_action: false,
        deferred_by_add_sign: true
      });
    } else {
      kept.push(item);
    }
  });

  if (!deferred.length) return kept;

  let insertAt = -1;
  kept.forEach((item, idx) => {
    if (groupKeys.has(item.key)) insertAt = idx;
  });
  if (insertAt < 0) return [...kept, ...deferred];

  const result = [...kept];
  result.splice(insertAt + 1, 0, ...deferred);
  return result;
}

/** 为当前待办节点补上「处理中」标识（转交/加签等场景下 runtime 合并可能遗漏） */
function markActiveProcessingLabel(items) {
  if (!items.length) return items;

  const hasAwaitingResubmit = items.some(
    (item) => item.status === 'current' && item.type === 'submit'
  );
  if (hasAwaitingResubmit) return items;

  let lastDoneIdx = -1;
  items.forEach((item, i) => {
    if (item.status === 'done' || item.done) lastDoneIdx = i;
  });

  let pickIdx = -1;
  let pickOrder = Infinity;
  for (let i = lastDoneIdx + 1; i < items.length; i += 1) {
    const item = items[i];
    if (item.queueKind === QUEUE_KIND.FUTURE) continue;
    if (item.type === 'cc' || item.type === 'submit') continue;
    if (item.is_add_sign_group) continue;
    if (item.deferred_by_add_sign) continue;
    if (item.done || item.status === 'done') continue;
    if (!ACTIVE_APPROVER_TYPES.has(item.type) && item.type_label !== '审批人') continue;
    const order = QUEUE_ORDER[item.queueKind] ?? QUEUE_ORDER[QUEUE_KIND.MAIN];
    if (order < pickOrder) {
      pickOrder = order;
      pickIdx = i;
    }
  }
  if (pickIdx < 0) return items;

  return items.map((item, i) => {
    if (item.done || item.status === 'done' || i <= lastDoneIdx) return item;
    if (item.queueKind === QUEUE_KIND.FUTURE || item.type === 'cc' || item.type === 'submit') {
      return item;
    }
    if (item.is_add_sign_group) return item;
    if (item.deferred_by_add_sign) return item;
    if (i === pickIdx) {
      return {
        ...item,
        status: 'current',
        action_label: '处理中',
        show_action: true
      };
    }
    if (item.status === 'current' || item.action_label === '处理中') {
      return {
        ...item,
        status: 'pending',
        action_label: null,
        show_action: false
      };
    }
    return item;
  });
}

function mapPendingStepForUser(step, index, user, userIndex = 0, isCurrent = false) {
  const nodeLabel = step.type === 'cc'
    ? (step.role_names?.length ? step.role_names.join('、') : null)
    : (step.label || step.type_label || null);
  return enrichProgressItem({
    key: `pending-${step.step_id || index}-${user?.id || userIndex}`,
    step_id: step.step_id || null,
    step_index: step.step_index ?? index,
    type: step.type,
    user_name: user?.name || '-',
    role_label: step.label || STEP_TYPE_LABELS[normalizeStepType(step.type)],
    action_label: isCurrent ? '处理中' : null,
    comment: null,
    create_time: null,
    status: isCurrent ? 'current' : 'pending',
    done: false,
    show_action: isCurrent
  }, step.type, nodeLabel);
}

function expandPendingStep(step, index, options = {}) {
  if (step.type === 'cc') {
    const item = buildCcProgressItem(step, index, options);
    return item ? [item] : [];
  }
  if (shouldMergeStepAssignees(step)) {
    const item = buildGroupStepProgressItem(step, index, options);
    return item ? [item] : [];
  }
  const assignees = (step.assignees || []).filter((u) => u?.name || u?.id);
  if (!assignees.length) return [];
  return assignees.map((u, i) => mapPendingStepForUser(step, index, u, i, options.isCurrent && i === 0));
}

function getTransferredOperatorNames(approvals) {
  const names = new Set();
  (approvals || []).forEach((a) => {
    if (a.action === 'transfer') names.add(a.approver_name);
  });
  return names;
}

function refreshTaskItemStatus(items, approveTasks) {
  const taskMap = Object.fromEntries(approveTasks.map((t) => [t.id, t]));
  return items.map((item) => {
    if (!item.task_id) return item;
    const task = taskMap[item.task_id];
    if (!task) return item;
    const isAddSign = task.add_sign_type === 'pre' || task.add_sign_type === 'post';
    if (task.status === 'done') {
      return {
        ...item,
        status: 'done',
        done: true,
        create_time: task.done_time || item.create_time,
        action_label: isAddSign ? '加签' : '同意',
        show_action: true
      };
    }
    return item;
  });
}

function parseReturnPending(process) {
  const raw = process?.return_pending;
  if (!raw) return null;
  if (typeof raw === 'object') return raw;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

/** 退回后（含已重新提交）的时间锚点：此时间点之前的审批记录不参与「已展示」判定 */
function getReturnReplayAnchorTime(approvals = []) {
  let lastReturnIdx = -1;
  approvals.forEach((a, idx) => {
    if (a.action === 'return') lastReturnIdx = idx;
  });
  if (lastReturnIdx < 0) return null;

  for (let i = lastReturnIdx + 1; i < approvals.length; i += 1) {
    if (approvals[i].action === 'submit') {
      return new Date(approvals[i].create_time).getTime();
    }
  }
  return new Date(approvals[lastReturnIdx].create_time).getTime();
}

function isItemAfterAnchor(item, anchorTime) {
  if (anchorTime == null) return true;
  if (!item?.create_time) return false;
  return new Date(item.create_time).getTime() > anchorTime;
}

/** 退回待重新提交时，按退回模式过滤将要走的后续节点（与后端 dispatch 逻辑对齐） */
function filterStepsForResubmitPreview(steps, pending) {
  if (!steps?.length || !pending) return steps || [];

  const fromIndex = pending.from_step_index;
  if (fromIndex == null || fromIndex < 0) return steps;

  const mode = pending.mode === 'direct' ? 'direct' : 'sequential';
  const toInitiator = pending.target_type === 'initiator' || pending.target_step_index === -1;

  if (toInitiator && mode === 'direct') {
    return steps.filter((s) => (s.step_index ?? 0) >= fromIndex);
  }

  if (!toInitiator && mode === 'direct') {
    return steps.filter((s) => (s.step_index ?? 0) >= fromIndex);
  }

  if (!toInitiator && mode === 'sequential') {
    const targetIndex = pending.target_step_index;
    if (targetIndex == null || targetIndex < 0) return steps;
    return steps.filter((s) => {
      const idx = s.step_index ?? 0;
      return (idx >= targetIndex && idx <= fromIndex) || idx > fromIndex;
    });
  }

  return steps;
}

/** 退回待重新提交时，后续节点仅作预览，不应继承模拟态的自动通过标记 */
function normalizeStepsForResubmitPreview(steps) {
  return (steps || []).map((s) => ({
    ...s,
    status: 'pending',
    auto_skipped: false
  }));
}

function buildRuntimeItems(process, approveTasks, usedTaskIds, shownNames) {
  const runtime = { current: [], future: [] };

  if (process.awaiting_resubmit) {
    runtime.current.push(enrichProgressItem({
      key: 'awaiting-resubmit',
      type: 'submit',
      user_name: process.applicant_name || '-',
      role_label: '发起人',
      action_label: '待重新提交',
      comment: null,
      create_time: null,
      status: 'current',
      done: false,
      show_action: true,
      queueKind: QUEUE_KIND.MAIN
    }, 'submit'));

    if (process.workflow?.steps?.length) {
      const pending = parseReturnPending(process);
      const filteredSteps = filterStepsForResubmitPreview(process.workflow.steps, pending);
      const previewProcess = {
        ...process,
        workflow: {
          ...process.workflow,
          steps: normalizeStepsForResubmitPreview(filteredSteps)
        }
      };
      buildWorkflowPreviewItems(previewProcess, {
        skipSubmitPreview: true,
        suppressAutoSkipPreview: true
      }).forEach((item) => {
        runtime.future.push({
          ...item,
          key: `after-resubmit-${item.key}`,
          queueKind: QUEUE_KIND.FUTURE
        });
      });
    }
    return runtime;
  }

  if (process.status !== 'pending' || !process.workflow?.has_workflow) return runtime;

  const steps = process.workflow.steps || [];
  const currentStep = steps.find((s) => s.status === 'current');
  const futureSteps = steps.filter((s) => s.status === 'pending');
  const transferredNames = getTransferredOperatorNames(process.approvals);

  if (currentStep) {
    const stepId = currentStep.step_id || process.current_step_id;
    const pendingTasks = approveTasks.filter(
      (t) => t.step_id === stepId && t.status === 'pending' && !usedTaskIds.has(t.id)
    );

    if (pendingTasks.length) {
      const pre = pendingTasks.filter((t) => t.add_sign_type === 'pre');
      const post = pendingTasks.filter((t) => t.add_sign_type === 'post');
      const main = pendingTasks.filter((t) => !t.add_sign_type);
      let markedCurrent = false;

      pre.forEach((task) => {
        usedTaskIds.add(task.id);
        shownNames.add(task.assignee_name);
        const isCurrent = !markedCurrent;
        if (isCurrent) markedCurrent = true;
        runtime.current.push(mapApproveTaskItem(task, {
          kind: 'add_sign_target',
          isCurrent,
          queueKind: QUEUE_KIND.PRE_ADD_SIGN
        }));
      });

      if (main.length && shouldMergeStepAssignees(currentStep)) {
        main.forEach((task) => {
          usedTaskIds.add(task.id);
          shownNames.add(task.assignee_name);
        });
        const mergedStep = {
          ...currentStep,
          assignees: main.map((t) => ({
            id: t.assignee_user_id,
            name: formatAssigneeDisplayName(t, 'approver')
          }))
        };
        const item = buildGroupStepProgressItem(mergedStep, 0, {
          isCurrent: !markedCurrent,
          queueKind: QUEUE_KIND.MAIN,
          taskId: main[0]?.id || null
        });
        if (item) {
          if (!markedCurrent) markedCurrent = true;
          runtime.current.push(item);
        }
      } else {
        main.forEach((task) => {
          usedTaskIds.add(task.id);
          shownNames.add(task.assignee_name);
          const isCurrent = !markedCurrent;
          if (isCurrent) markedCurrent = true;
          runtime.current.push(mapApproveTaskItem(task, {
            kind: 'approver',
            isCurrent,
            queueKind: QUEUE_KIND.MAIN
          }));
        });
      }

      post.forEach((task) => {
        usedTaskIds.add(task.id);
        shownNames.add(task.assignee_name);
        const isCurrent = !markedCurrent;
        if (isCurrent) markedCurrent = true;
        runtime.current.push(mapApproveTaskItem(task, {
          kind: 'add_sign_target',
          isCurrent,
          queueKind: QUEUE_KIND.POST_ADD_SIGN
        }));
      });
    } else if (currentStep.type === 'approver') {
      const assignees = (currentStep.assignees || []).filter(
        (u) => u?.name && !shownNames.has(u.name) && !transferredNames.has(u.name)
      );
      if (shouldMergeStepAssignees(currentStep)) {
        assignees.forEach((u) => shownNames.add(u.name));
        const item = buildGroupStepProgressItem(currentStep, 0, {
          isCurrent: true,
          queueKind: QUEUE_KIND.MAIN
        });
        if (item) runtime.current.push(item);
      } else {
        assignees.forEach((u, i) => {
          shownNames.add(u.name);
          runtime.current.push({
            ...mapPendingStepForUser(currentStep, 0, u, i, i === 0),
            queueKind: QUEUE_KIND.MAIN
          });
        });
      }
    } else if (currentStep.type === 'cc') {
      const item = buildCcProgressItem(currentStep, 0, {
        isCurrent: true,
        queueKind: QUEUE_KIND.MAIN
      });
      if (item) runtime.current.push(item);
    } else if (currentStep.type === 'executor') {
      expandPendingStep(currentStep, 0, { isCurrent: true, queueKind: QUEUE_KIND.MAIN }).forEach((item) => {
        if (shouldSkipRuntimeAssignee(item, shownNames)) return;
        runtime.current.push(item);
      });
    }
  }

  futureSteps.forEach((step, idx) => {
    const stepIndex = step.step_index ?? idx;
    if (step.type === 'cc') {
      const item = buildCcProgressItem(step, idx, { queueKind: QUEUE_KIND.FUTURE });
      if (item) {
        runtime.future.push({
          ...item,
          step_index: stepIndex,
          action_label: null,
          show_action: false
        });
      }
      return;
    }
    expandPendingStep(step, idx).forEach((item) => {
      if (shouldSkipRuntimeAssignee(item, shownNames)) return;
      runtime.future.push({
        ...item,
        step_index: stepIndex,
        action_label: null,
        show_action: false,
        queueKind: QUEUE_KIND.FUTURE
      });
    });
  });

  return runtime;
}

/** 待办区排序：前加签 → 主审批/转交对象 → 后加签 → 后续节点 */
function reorderPendingTail(items) {
  let lastDoneIdx = -1;
  items.forEach((item, i) => {
    if (item.status === 'done' || item.done) lastDoneIdx = i;
  });

  const head = items.slice(0, lastDoneIdx + 1);
  const tail = items.slice(lastDoneIdx + 1);
  if (!tail.length) return items;

  const future = tail.filter((i) => i.queueKind === QUEUE_KIND.FUTURE || i.type === 'cc');
  const active = tail.filter((i) => i.queueKind !== QUEUE_KIND.FUTURE && i.type !== 'cc');

  future.sort((a, b) => {
    const ia = a.step_index;
    const ib = b.step_index;
    if (ia != null && ib != null && ia !== ib) return ia - ib;
    if (ia != null && ib == null) return -1;
    if (ia == null && ib != null) return 1;
    return 0;
  });

  active.sort((a, b) => {
    const orderA = QUEUE_ORDER[a.queueKind] ?? QUEUE_ORDER[QUEUE_KIND.MAIN];
    const orderB = QUEUE_ORDER[b.queueKind] ?? QUEUE_ORDER[QUEUE_KIND.MAIN];
    if (orderA !== orderB) return orderA - orderB;
    if (a.status === 'current' && b.status !== 'current') return -1;
    if (b.status === 'current' && a.status !== 'current') return 1;
    const ta = a.create_time ? new Date(a.create_time).getTime() : 0;
    const tb = b.create_time ? new Date(b.create_time).getTime() : 0;
    return ta - tb;
  });

  return [...head, ...active, ...future];
}

function sortDoneThenPending(items) {
  const done = items.filter((i) => i.status === 'done' || i.done);
  const pending = items.filter((i) => !(i.status === 'done' || i.done));
  done.sort((a, b) => {
    if (!a.create_time || !b.create_time) return 0;
    return new Date(a.create_time) - new Date(b.create_time);
  });
  return [...done, ...pending];
}

function dedupeCcProgressItems(items) {
  const seenStepIds = new Set();
  const seenNames = new Set();
  return items.filter((item) => {
    if (item.type !== 'cc') return true;
    if (item.step_id) {
      if (seenStepIds.has(item.step_id)) return false;
      seenStepIds.add(item.step_id);
      return true;
    }
    const names = splitProgressUserNames(item.user_name);
    const nameKey = names.sort().join('|');
    if (seenNames.has(nameKey)) return false;
    seenNames.add(nameKey);
    return true;
  });
}

function buildAssigneePickPreviewItems(step, idx, assigneePick) {
  const stepId = step.step_id;
  const selectedIds = assigneePick?.value?.[stepId] || [];
  const userMap = assigneePick?.userMap || {};
  const multiMode = step.multi_mode || 'or';
  const allowMultiple = step.type === 'cc' || multiMode !== 'sequential';
  const roleLabel = step.label || step.type_label || (step.type === 'cc' ? '抄送人' : '审批人');
  const nodeLabel = step.type === 'cc'
    ? (step.role_names?.length ? step.role_names.join('、') : (step.label || null))
    : (step.label || step.type_label || null);

  const selectedUsers = selectedIds
    .map((id) => ({ id, name: userMap[String(id)] || String(id) }))
    .filter((u) => u.name);

  const items = [];

  selectedUsers.forEach((user, i) => {
    items.push(enrichProgressItem({
      key: `preview-pick-user-${stepId || idx}-${user.id}`,
      step_id: stepId,
      type: step.type,
      user_name: user.name,
      role_label: roleLabel,
      action_label: null,
      comment: null,
      create_time: null,
      status: 'pending',
      done: false,
      show_action: false,
      queueKind: QUEUE_KIND.FUTURE,
      multi_mode: multiMode,
      multi_mode_label: step.multi_mode_label || null
    }, step.type, nodeLabel));
  });

  if (!selectedUsers.length || (allowMultiple && selectedUsers.length > 0)) {
    const showAddSlot = !selectedUsers.length || allowMultiple;
    if (showAddSlot) {
      items.push({
        key: `preview-pick-${stepId || idx}`,
        is_assignee_pick: true,
        step_id: stepId,
        type: step.type,
        user_name: '',
        role_label: roleLabel,
        node_label: nodeLabel,
        action_label: null,
        comment: null,
        create_time: null,
        status: 'pending',
        done: false,
        show_action: false,
        queueKind: QUEUE_KIND.FUTURE,
        multi_mode: multiMode,
        allow_multiple: allowMultiple,
        pick_hint: selectedUsers.length
          ? '继续添加'
          : (step.type === 'cc' ? '请添加抄送人' : '请添加审批人')
      });
    }
  }

  return items;
}

function collectDoneApproverNames(items, anchorTime = null) {
  const names = new Set();
  (items || []).forEach((item) => {
    if (!(item.done || item.status === 'done')) return;
    if (item.queueKind === QUEUE_KIND.FUTURE) return;
    if (item.type === 'submit' || item.type === 'cc' || item.type === 'executor') return;
    if (!ACTIVE_APPROVER_TYPES.has(item.type) && item.type !== 'approve') return;
    if (!isItemAfterAnchor(item, anchorTime)) return;
    splitProgressUserNames(item.user_name).forEach((name) => names.add(name));
  });
  return names;
}

function isStepApprovalAlreadyShown(step, existingItems, anchorTime = null) {
  if (!step?.step_id) return false;
  if (existingItems.some((item) => (
    item.step_id === step.step_id
    && (item.type === 'approver' || item.type === 'approve')
    && (item.done || item.status === 'done')
    && isItemAfterAnchor(item, anchorTime)
  ))) {
    return true;
  }

  const assigneeNames = getAssigneeNamesFromStep(step);
  if (!assigneeNames.length) return false;

  const doneNames = collectDoneApproverNames(existingItems, anchorTime);
  const matched = assigneeNames.filter((name) => doneNames.has(name));
  if (!matched.length) return false;

  const mode = step.multi_mode || 'or';
  if (mode === 'and') {
    return matched.length === assigneeNames.length;
  }
  return true;
}

function isSameCompletedApproverProgress(existing, candidate) {
  if (!existing || !candidate) return false;
  if (existing.step_id && candidate.step_id && existing.step_id === candidate.step_id) {
    return existing.type === 'approver' || candidate.type === 'approver';
  }
  if ((existing.done || existing.status === 'done')
    && (candidate.done || candidate.status === 'done')
    && existing.user_name === candidate.user_name
    && (existing.type === 'approver' || existing.type === 'approve')
    && (candidate.type === 'approver' || candidate.type === 'approve')) {
    return true;
  }
  return false;
}

/** 补全工作流中已自动通过、但审批记录尚未体现在时间线上的审批节点 */
function buildCompletedWorkflowApproverItems(process, existingItems) {
  const workflowSteps = process.workflow?.steps || [];
  const anchorTime = getReturnReplayAnchorTime(process.approvals);
  const currentStepIndex = workflowSteps.find((s) => s.status === 'current')?.step_index
    ?? process.current_step_index
    ?? null;

  const isStepAlreadyPassed = (step) => {
    const idx = step.step_index ?? 0;
    if (step.status === 'done') return true;
    if (currentStepIndex != null && idx < currentStepIndex) return true;
    return false;
  };

  const steps = workflowSteps
    .filter((s) => s.type === 'approver' && isStepAlreadyPassed(s))
    .sort((a, b) => (a.step_index ?? 0) - (b.step_index ?? 0));

  const items = [];
  steps.forEach((step, idx) => {
    if (!step.step_id) return;
    if (isStepApprovalAlreadyShown(step, existingItems, anchorTime)) return;

    const isAuto = step.auto_skipped || step.status === 'skipped';
    // 自动通过节点以审批记录为准，避免模拟态重复/误补
    if (isAuto) return;
    if (shouldMergeStepAssignees(step)) {
      const item = buildGroupStepProgressItem(step, idx, {
        status: 'done',
        done: true,
        actionLabel: isAuto ? '自动通过' : '同意',
        queueKind: QUEUE_KIND.MAIN,
        keyPrefix: 'workflow-done'
      });
      if (item) items.push(item);
      return;
    }

    (step.assignees || []).forEach((u, i) => {
      if (!u?.name) return;
      items.push(enrichProgressItem({
        key: `workflow-done-${step.step_id}-${u.id || i}`,
        step_id: step.step_id,
        step_index: step.step_index ?? idx,
        type: 'approver',
        user_name: u.name,
        role_label: step.label || STEP_TYPE_LABELS.approver,
        action_label: isAuto ? '自动通过' : '同意',
        comment: isAuto ? '自动通过' : null,
        create_time: null,
        status: 'done',
        done: true,
        show_action: true,
        queueKind: QUEUE_KIND.MAIN
      }, 'approver', step.label || null));
    });
  });
  return items;
}

function buildWorkflowPreviewItems(process, options = {}) {
  const { assigneePick, skipSubmitPreview = false, suppressAutoSkipPreview = false } = options;
  const steps = process.workflow?.steps || [];
  const items = [];

  if (process.applicant_name && !skipSubmitPreview) {
    items.push(enrichProgressItem({
      key: 'preview-submit',
      type: 'submit',
      user_name: process.applicant_name,
      role_label: '发起人',
      action_label: '提交申请',
      comment: null,
      create_time: null,
      status: 'done',
      done: true,
      show_action: true,
      queueKind: QUEUE_KIND.MAIN
    }, 'submit'));
  }

  if (!steps.length) return items;

  steps.forEach((step, idx) => {
    if (step.status === 'skipped' && !(step.assignees || []).length) return;

    const nodeLabel = step.type === 'cc'
      ? (step.role_names?.length ? step.role_names.join('、') : (step.label || null))
      : (step.label || step.type_label || null);

    if (step.status === 'paused') {
      items.push(enrichProgressItem({
        key: `preview-paused-${step.step_id || idx}`,
        type: step.type,
        user_name: '-',
        role_label: nodeLabel || STEP_TYPE_LABELS[normalizeStepType(step.type)],
        action_label: null,
        comment: step.pause_reason || '流程将在此暂停',
        status: 'pending',
        done: false,
        show_action: false,
        queueKind: QUEUE_KIND.FUTURE
      }, step.type, nodeLabel));
      return;
    }

    if (step.awaiting_assignee_pick && ['approver', 'cc'].includes(step.type)) {
      buildAssigneePickPreviewItems(step, idx, assigneePick).forEach((item) => items.push(item));
      return;
    }

    const assignees = (step.assignees || []).filter((u) => u?.name || u?.id);
    if (!assignees.length && ['approver', 'executor', 'cc'].includes(step.type)) {
      return;
    }

    const isAutoSkipped = !suppressAutoSkipPreview
      && (step.status === 'skipped' || step.auto_skipped);
    expandPendingStep(step, idx, { queueKind: QUEUE_KIND.FUTURE }).forEach((item) => {
      items.push({
        ...item,
        step_index: step.step_index ?? idx,
        action_label: isAutoSkipped ? '自动通过' : null,
        show_action: isAutoSkipped,
        done: isAutoSkipped,
        status: isAutoSkipped ? 'done' : (item.status || 'pending'),
        queueKind: QUEUE_KIND.FUTURE
      });
    });
  });

  return items;
}

export function buildProgressItems(process, options = {}) {
  const approvals = process.approvals || [];
  const approveTasks = process.approve_tasks || [];

  if (!approvals.length && !approveTasks.length) {
    if (process.workflow?.steps?.length || (process.status === 'draft' && process.workflow?.has_workflow)) {
      const previewItems = buildWorkflowPreviewItems(process, options);
      if (previewItems.length) return previewItems;
    }
  }
  const usedTaskIds = new Set();
  const shownNames = new Set();
  const items = [];

  approvals.forEach((a) => {
    if (shouldSkipApproveLog(a, approveTasks, usedTaskIds)) return;

    if (a.action === 'transfer') {
      const relatedTasks = matchTasksForApproval(a, approveTasks, usedTaskIds);
      const targetNames = parseTransferTargets(a.comment);
      const operatorNode = {
        ...mapApprovalItem(a),
        comment: a.comment || null,
        action_label: '转交',
        show_action: true
      };
      if (isTerminalProcessStatus(process.status)) {
        items.push(operatorNode);
        return;
      }
      const targetNodes = [];
      let markedTransferCurrent = false;
      relatedTasks.forEach((t, i) => {
        usedTaskIds.add(t.id);
        shownNames.add(t.assignee_name);
        const isCurrent = t.status === 'pending' && !markedTransferCurrent;
        if (isCurrent) markedTransferCurrent = true;
        targetNodes.push(mapApproveTaskItem(t, {
          kind: 'transfer_target',
          sortTime: offsetTime(a.create_time, i + 1),
          queueKind: QUEUE_KIND.MAIN,
          isCurrent
        }));
      });
      targetNames.forEach((name, i) => {
        if (!targetNodes.some((n) => n.user_name === name)) {
          const isCurrent = !markedTransferCurrent;
          if (isCurrent) markedTransferCurrent = true;
          targetNodes.push(mapPlaceholderTarget(
            name, `transfer-${a.id}-${i}`, isCurrent ? 'current' : 'pending',
            offsetTime(a.create_time, i + 1), QUEUE_KIND.MAIN
          ));
        }
      });
      items.push(operatorNode, ...targetNodes);
      return;
    }

    if (a.action === 'add_sign') {
      const operatorNode = {
        ...mapApprovalItem(a),
        comment: a.comment || null,
        action_label: '加签',
        show_action: true
      };
      items.push(operatorNode);
      if (a.approver_name) shownNames.add(a.approver_name);

      if (!isTerminalProcessStatus(process.status)) {
        const groupItem = buildAddSignGroupItem(a, approveTasks, usedTaskIds, shownNames);
        if (groupItem) items.push(groupItem);
      }
      return;
    }

    items.push(mapApprovalItem(a));
    if (a.approver_name && a.action !== 'submit' && a.action !== 'revoke') {
      shownNames.add(progressAssigneeKey({ type: 'approver', user_name: a.approver_name, step_id: null }));
    }
  });

  buildCompletedWorkflowApproverItems(process, items).forEach((item) => {
    if (!items.some((existing) => isSameCompletedApproverProgress(existing, item))) {
      items.push(item);
    }
  });

  buildCcTaskGroupItems(process.cc_tasks).forEach((item) => {
    if (!items.some((i) => i.type === 'cc' && i.step_id && item.step_id && i.step_id === item.step_id)) {
      items.push(item);
    }
  });

  buildExecuteTaskGroupItems(process.execute_tasks, process).forEach((item) => {
    if (!items.some((i) => isSameExecutorProgress(i, item))) {
      items.push(item);
    }
  });

  // 有待走工作流模拟时，后续节点（含执行人）由 buildRuntimeItems 按 step_index 顺序追加，
  // 避免 buildWorkflowExecutorFallbackItems 抢先插入导致执行节点跑到审批/抄送之前。
  const useWorkflowFutureSteps = process.status === 'pending' && process.workflow?.has_workflow;
  if (!useWorkflowFutureSteps) {
    buildWorkflowExecutorFallbackItems(process, items).forEach((item) => {
      if (!items.some((i) => isSameExecutorProgress(i, item))) {
        items.push(item);
      }
    });
  }

  const sortedItems = sortDoneThenPending(items);

  const runtime = buildRuntimeItems(process, approveTasks, usedTaskIds, shownNames);

  const merged = [...sortedItems];
  runtime.current.forEach((node) => {
    const idx = merged.findIndex((i) => (
      (node.task_id && i.task_id === node.task_id)
      || (
        i.user_name === node.user_name
        && !i.done
        && i.type !== 'cc'
        && i.type !== 'submit'
        && i.type !== 'executor'
        && node.type !== 'executor'
      )
    ));
    if (idx >= 0) {
      merged[idx] = { ...merged[idx], ...node };
    } else {
      merged.push(node);
    }
  });
  runtime.future.forEach((node) => {
    if (!isFutureNodeDuplicate(merged, node)) {
      merged.push(node);
    }
  });

  const refreshed = reorderPendingTail(refreshTaskItemStatus(merged, approveTasks));
  const processFinished = process.status === 'approved' || process.status === 'completed';
  const withGroups = refreshAddSignGroups(refreshed, approveTasks, approvals, { finished: processFinished });
  const deduped = removeDuplicateAddSignParticipants(withGroups);
  const addSignOrdered = reorderAddSignGroupAfterAction(deduped);
  const addSignDeferred = deferPeerApproversDuringAddSign(addSignOrdered);
  const ccDeduped = dedupeCcProgressItems(addSignDeferred);
  if (isTerminalProcessStatus(process.status)) {
    return filterTerminalProgressItems(ccDeduped, process);
  }
  return markActiveProcessingLabel(ccDeduped);
}

export function buildChangeLogRows(process) {
  return (process.approvals || []).map((a) => ({
    key: a.id,
    time: a.create_time,
    user: a.approver_name || '-',
    action: a.action === 'submit' ? '提交'
      : a.action === 'approve' ? '同意'
      : a.action === 'reject' ? '驳回'
      : a.action === 'transfer' ? '转交'
      : a.action === 'add_sign' ? '加签'
      : a.action === 'return' ? '退回'
      : a.action === 'revoke' ? '撤销'
      : a.action,
    comment: a.comment || '-'
  }));
}
